import { useCallback, useEffect, useId, useRef, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";

/**
 * SDK-driven data hooks for the custom dbm. chat workspace.
 * These wrap the CometChat JS SDK directly — no UI Kit components.
 */

export type Selection =
  | {
      kind: "group";
      id: string;
      name: string;
      subtitle?: string;
      membersCount?: number;
      isStructuredChannel?: boolean;
      /** Scroll to and highlight this message on open (jumping back to a
       * saved message). Ignored if it isn't in the loaded history. */
      focusMessageId?: number;
    }
  | { kind: "user"; id: string; name: string; subtitle?: string; org?: string; focusMessageId?: number };

/** Fired whenever a DM's messages get marked read — see useMessages'
 * markRead and useDirectMessages' unreadOverrides below. */
const DM_READ_EVENT = "dbm-dm-read";

/** Groups created via "Create a group" (casual, calling-enabled, no
 * directory) are tagged in metadata so they can be told apart from
 * structured "Channels" — both are CometChat.Group under the hood. */
export function isDbmGroup(g: CometChat.Group): boolean {
  let meta: any = g.getMetadata?.() || {};
  if (typeof meta === "string") {
    try {
      meta = JSON.parse(meta);
    } catch {
      meta = {};
    }
  }
  return meta?.dbmKind === "group";
}

/** Channels = structured, joined groups (public/private, directory-listed,
 * text-only) — anything NOT tagged as a casual "Group". */
export function useChannels() {
  const [groups, setGroups] = useState<CometChat.Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  // This hook is mounted by more than one component at once (the Sidebar
  // permanently, the search popup transiently) — a shared fixed listener
  // ID would mean whichever mounts last "owns" it, and unmounting THAT
  // instance deregisters the listener for every other instance too. A
  // per-instance id keeps them independent.
  const instanceId = useId();

  useEffect(() => {
    let cancelled = false;
    // Only show the loading state on the very first fetch — a live-triggered
    // refetch shouldn't blank the list out and flash "Loading…" over it.
    if (version === 0) setLoading(true);
    const req = new CometChat.GroupsRequestBuilder().setLimit(50).build();
    req
      .fetchNext()
      .then((list) => {
        if (!cancelled) setGroups((list as CometChat.Group[]).filter((g) => !isDbmGroup(g)));
      })
      .catch((e) => console.warn("channels load failed", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  // Live updates: any channel message means the list/ordering may have
  // changed — without this, new activity only shows up after a refresh.
  useEffect(() => {
    const id = `dbm-channel-list-listener-${instanceId}`;
    const bump = (msg: CometChat.BaseMessage) => {
      if (msg.getReceiverType() === "group") refetch();
    };
    CometChat.addMessageListener(
      id,
      new CometChat.MessageListener({
        onTextMessageReceived: bump,
        onMediaMessageReceived: bump,
        onCustomMessageReceived: bump,
      })
    );
    return () => CometChat.removeMessageListener(id);
  }, [refetch, instanceId]);

  // Shows a just-created channel immediately, without waiting on a refetch
  // that can race the backend's own indexing of a group created
  // milliseconds ago — that race is why a brand-new channel used to stay
  // invisible until something else (e.g. sending a message) forced a
  // later refetch to catch it.
  const addLocal = useCallback((group: CometChat.Group) => {
    setGroups((prev) =>
      prev.some((g) => g.getGuid() === group.getGuid()) ? prev : [group, ...prev]
    );
  }, []);

  return { groups, loading, refetch, addLocal };
}

/** Groups = casual joined groups, tagged dbmKind:"group" in metadata —
 * quick chat + calling with a few people, no public directory. */
export function useGroups() {
  const [groups, setGroups] = useState<CometChat.Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  // Same per-instance-id reasoning as useChannels above.
  const instanceId = useId();

  useEffect(() => {
    let cancelled = false;
    if (version === 0) setLoading(true);
    const req = new CometChat.GroupsRequestBuilder().setLimit(50).build();
    req
      .fetchNext()
      .then((list) => {
        if (!cancelled) setGroups((list as CometChat.Group[]).filter(isDbmGroup));
      })
      .catch((e) => console.warn("groups load failed", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    const id = `dbm-group-list-listener-${instanceId}`;
    const bump = (msg: CometChat.BaseMessage) => {
      if (msg.getReceiverType() === "group") refetch();
    };
    CometChat.addMessageListener(
      id,
      new CometChat.MessageListener({
        onTextMessageReceived: bump,
        onMediaMessageReceived: bump,
        onCustomMessageReceived: bump,
      })
    );
    return () => CometChat.removeMessageListener(id);
  }, [refetch, instanceId]);

  // Same "show it immediately" reasoning as useChannels.addLocal above.
  const addLocal = useCallback((group: CometChat.Group) => {
    setGroups((prev) =>
      prev.some((g) => g.getGuid() === group.getGuid()) ? prev : [group, ...prev]
    );
  }, []);

  return { groups, loading, refetch, addLocal };
}

/** Direct messages = 1-on-1 conversations. */
export function useDirectMessages() {
  const [conversations, setConversations] = useState<CometChat.Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  // Live online/offline per uid, updated directly from the presence
  // EVENT itself rather than derived from a re-fetched conversation list.
  // A re-fetch races whatever caching/latency the conversations endpoint
  // has around presence — the event payload is the presence change, so
  // reading straight from it is the only way the dot can't lag behind
  // reality.
  const [presence, setPresence] = useState<Record<string, boolean>>({});
  // Optimistic "this conversation's unread count is 0 right now" per uid —
  // set the instant a DM you have open gets marked read (see the
  // DM_READ_EVENT listener below), same lagging-snapshot problem as
  // presence: markAsRead happens in a different hook instance entirely
  // (useMessages, owned by MessagePane) that has no other way to tell
  // this hook's own conversation snapshot to update. Cleared the moment a
  // NEW message arrives for that uid, so it can never mask a genuinely
  // unread later message.
  const [unreadOverrides, setUnreadOverrides] = useState<Record<string, number>>({});
  // Same per-instance-id reasoning as useChannels above — this hook is
  // also mounted by both the Sidebar and the search popup at once.
  const instanceId = useId();

  useEffect(() => {
    let cancelled = false;
    if (version === 0) setLoading(true);
    const req = new CometChat.ConversationsRequestBuilder()
      .setConversationType("user")
      .setLimit(50)
      .build();
    req
      .fetchNext()
      .then((list) => {
        if (!cancelled) setConversations(list as CometChat.Conversation[]);
      })
      .catch((e) => console.warn("DMs load failed", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  // Live updates: any 1:1 message bumps a re-fetch so new conversations,
  // reordering, and unread counts stay current without a page refresh.
  useEffect(() => {
    const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();
    const id = `dbm-dm-list-listener-${instanceId}`;
    const bump = (msg: CometChat.BaseMessage) => {
      if (msg.getReceiverType() !== "user") return;
      // A fresh message for this uid means any earlier "read" override is
      // no longer trustworthy — let the refetch's real server count win.
      const senderUid = msg.getSender()?.getUid();
      const otherUid = senderUid === meUid ? msg.getReceiverId() : senderUid;
      if (otherUid) {
        setUnreadOverrides((prev) => {
          if (!(otherUid in prev)) return prev;
          const next = { ...prev };
          delete next[otherUid];
          return next;
        });
      }
      setVersion((v) => v + 1);
    };
    CometChat.addMessageListener(
      id,
      new CometChat.MessageListener({
        onTextMessageReceived: bump,
        onMediaMessageReceived: bump,
        onCustomMessageReceived: bump,
      })
    );
    // Presence changes update the live map directly — see `presence` above.
    const presenceId = `dbm-dm-presence-${instanceId}`;
    CometChat.addUserListener(
      presenceId,
      new CometChat.UserListener({
        onUserOnline: (u: CometChat.User) => setPresence((p) => ({ ...p, [u.getUid()]: true })),
        onUserOffline: (u: CometChat.User) => setPresence((p) => ({ ...p, [u.getUid()]: false })),
      })
    );
    const onRead = (e: Event) => {
      const uid = (e as CustomEvent<{ uid: string }>).detail?.uid;
      if (uid) setUnreadOverrides((prev) => ({ ...prev, [uid]: 0 }));
    };
    window.addEventListener(DM_READ_EVENT, onRead);
    return () => {
      CometChat.removeMessageListener(id);
      CometChat.removeUserListener(presenceId);
      window.removeEventListener(DM_READ_EVENT, onRead);
    };
  }, [instanceId]);

  return { conversations, loading, presence, unreadOverrides };
}

/* usePartners() lived here — a flat "everyone tagged SUPPLIER/CUSTOMER"
   sidebar section. Removed along with the section itself: it fetched the
   whole directory unfiltered (the exact thing the domain restrictions
   now govern), and the categorised Teams/Suppliers/Customers/Partners
   structure that replaces it is a separate piece of work. Anything
   needing a people list now goes through directoryScope.ts. */

/** Members of a channel — for the header facepile + count, plus the
 * logged-in user's OWN scope/owner status in it (for permission checks).
 *
 * `myScope`/`owner` come from CometChat.getGroup(guid) rather than by
 * scanning `members` for your own row — Group.getScope() is CometChat's
 * own authoritative answer to "what am I in this group," and doesn't
 * depend on the members page having loaded/included you yet (which, for
 * a channel you just created, it often hasn't). */
export function useGroupMembers(guid: string | null) {
  const [members, setMembers] = useState<CometChat.GroupMember[]>([]);
  const [myScope, setMyScope] = useState<string | null>(null);
  const [owner, setOwner] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const instanceId = useId();

  useEffect(() => {
    if (!guid) {
      setMembers([]);
      setMyScope(null);
      setOwner(null);
      return;
    }
    let cancelled = false;
    const req = new CometChat.GroupMembersRequestBuilder(guid).setLimit(100).build();
    req
      .fetchNext()
      .then((list) => {
        if (!cancelled) setMembers(list as CometChat.GroupMember[]);
      })
      .catch((e) => console.warn("group members load failed", e));
    CometChat.getGroup(guid)
      .then((g) => {
        if (cancelled) return;
        setMyScope(g.getScope() || null);
        setOwner(g.getOwner() || null);
      })
      .catch((e) => console.warn("group scope load failed", e));
    return () => {
      cancelled = true;
    };
  }, [guid, version]);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  // Applied immediately after the acting user's own promote/demote/remove
  // succeeds, instead of waiting on a fresh fetchNext() — CometChat's own
  // real-time GroupListener below only fires for members watching from
  // ANOTHER session, never as an echo of your own action, and the
  // member-list REST endpoint isn't guaranteed to reflect a change made
  // a moment earlier, which is what made this look like "nothing updates
  // until you refresh the page" for the person who just made the change.
  const patchScope = useCallback((uid: string, scope: string) => {
    setMembers((prev) => {
      const next = prev.slice();
      const idx = next.findIndex((m) => m.getUid() === uid);
      if (idx >= 0) {
        next[idx].setScope(scope as CometChat.GroupMemberScope);
      }
      return next;
    });
  }, []);
  const removeMemberLocal = useCallback((uid: string) => {
    setMembers((prev) => prev.filter((m) => m.getUid() !== uid));
  }, []);

  // Live membership changes — someone else being added/kicked/leaving/
  // promoted while you have this channel's Details panel open, without
  // this the member list (and every permission check derived from it)
  // only ever updates on next refetch.
  useEffect(() => {
    if (!guid) return;
    const id = `dbm-group-members-listener-${instanceId}`;
    const matches = (g: CometChat.Group) => g.getGuid() === guid;
    CometChat.addGroupListener(
      id,
      new CometChat.GroupListener({
        onGroupMemberJoined: (_m: CometChat.BaseMessage, _u: CometChat.GroupMember, g: CometChat.Group) =>
          matches(g) && refetch(),
        onGroupMemberLeft: (_m: CometChat.BaseMessage, _u: CometChat.GroupMember, g: CometChat.Group) =>
          matches(g) && refetch(),
        onGroupMemberKicked: (_m: CometChat.BaseMessage, _u: CometChat.User, _by: CometChat.User, g: CometChat.Group) =>
          matches(g) && refetch(),
        onGroupMemberBanned: (_m: CometChat.BaseMessage, _u: CometChat.User, _by: CometChat.User, g: CometChat.Group) =>
          matches(g) && refetch(),
        onGroupMemberScopeChanged: (
          _m: CometChat.BaseMessage,
          _u: CometChat.GroupMember,
          _newScope: string,
          _oldScope: string,
          g: CometChat.Group
        ) => matches(g) && refetch(),
        onMemberAddedToGroup: (_m: CometChat.BaseMessage, _u: CometChat.User, _by: CometChat.User, g: CometChat.Group) =>
          matches(g) && refetch(),
      })
    );
    return () => CometChat.removeGroupListener(id);
  }, [guid, refetch, instanceId]);

  return { members, myScope, owner, refetch, patchScope, removeMemberLocal };
}

/**
 * Message history + live updates for the selected conversation.
 * Fetches the last page, then appends via a scoped message listener.
 */
export function useMessages(selection: Selection | null) {
  const [messages, setMessages] = useState<CometChat.BaseMessage[]>([]);
  const [loading, setLoading] = useState(false);
  // Who (if anyone) is typing in this conversation right now.
  const [typingFrom, setTypingFrom] = useState<string | null>(null);
  // Receipt watermarks for own-message ticks (1:1 only) — any own message
  // with id <= watermark has been delivered/read by the other person.
  const [deliveredUpTo, setDeliveredUpTo] = useState(0);
  const [readUpTo, setReadUpTo] = useState(0);
  // Bumped on every group read receipt for the open conversation —
  // GroupSeenIndicator has no live listener of its own (it fetches a
  // per-message reader list over REST), so this is its only signal that
  // "someone in this group just read something, go refetch." Without it,
  // each message's reader list only ever got fetched once on mount and
  // then went stale for the rest of the session.
  const [groupReadPulse, setGroupReadPulse] = useState(0);
  const listenerId = useRef<string>("dbm-msg-listener");
  const typingClearTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!selection) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setTypingFrom(null);
    setDeliveredUpTo(0);
    setReadUpTo(0);
    setGroupReadPulse(0);

    const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();

    const markRead = (msg: CometChat.BaseMessage) => {
      // Marking as read powers the sender's ✓✓ and clears server-side
      // unread counts for this conversation.
      if (msg.getSender()?.getUid() === meUid) return;
      try {
        void CometChat.markAsRead(msg);
      } catch {
        /* ignore */
      }
      // The Sidebar's DM list is a separate hook instance holding its own
      // conversation snapshot (including unread counts) — marking read
      // here doesn't touch it. Without this event, that unread badge
      // stayed stuck at whatever it was when the message arrived, even
      // while you were already looking straight at it, until something
      // else forced a full refetch.
      if (selection.kind === "user") {
        window.dispatchEvent(new CustomEvent(DM_READ_EVENT, { detail: { uid: selection.id } }));
      }
    };

    const builder = new CometChat.MessagesRequestBuilder().setLimit(40).hideReplies(true);
    if (selection.kind === "group") builder.setGUID(selection.id);
    else builder.setUID(selection.id);
    const req = builder.build();

    req
      .fetchPrevious()
      .then((list) => {
        const messages = ((list as CometChat.BaseMessage[]) || []).filter(
          (m) => m.getCategory() !== CometChat.CATEGORY_ACTION
        );
        if (!cancelled) {
          setMessages(messages);
          const last = messages[messages.length - 1];
          if (last) markRead(last);
        }
      })
      .catch((e) => console.warn("messages load failed", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Live updates scoped to the open conversation.
    const id = `${listenerId.current}-${selection.kind}-${selection.id}`;
    const belongsHere = (msg: CometChat.BaseMessage): boolean => {
      if (selection.kind === "group") {
        return msg.getReceiverType() === "group" && msg.getReceiverId() === selection.id;
      }
      // 1:1 — a message belongs if it's to or from the selected user.
      const senderId = msg.getSender()?.getUid();
      const receiverId = msg.getReceiverId();
      return (
        msg.getReceiverType() === "user" &&
        (senderId === selection.id || receiverId === selection.id)
      );
    };

    const append = (msg: CometChat.BaseMessage) => {
      if (!belongsHere(msg)) return;
      setMessages((prev) => {
        if (prev.some((m) => m.getId() === msg.getId())) return prev;
        return [...prev, msg];
      });
      markRead(msg);
      // A new message from them supersedes their typing state.
      if (msg.getSender()?.getUid() !== meUid) setTypingFrom(null);
    };

    const replace = (msg: CometChat.BaseMessage) => {
      if (!belongsHere(msg)) return;
      setMessages((prev) => prev.map((m) => (m.getId() === msg.getId() ? msg : m)));
    };

    const typingMatches = (typing: CometChat.TypingIndicator): boolean => {
      if (selection.kind === "group") {
        return typing.getReceiverType() === "group" && typing.getReceiverId() === selection.id;
      }
      return typing.getSender()?.getUid() === selection.id;
    };

    const receiptMatches = (receipt: CometChat.MessageReceipt): boolean =>
      selection.kind === "user" && receipt.getSender()?.getUid() === selection.id;

    CometChat.addMessageListener(
      id,
      new CometChat.MessageListener({
        onTextMessageReceived: append,
        onMediaMessageReceived: append,
        onCustomMessageReceived: append,
        onMessageEdited: replace,
        onMessageDeleted: replace,
        onTypingStarted: (typing: CometChat.TypingIndicator) => {
          if (!typingMatches(typing)) return;
          setTypingFrom(typing.getSender()?.getName() || "Someone");
          // Safety: never leave a stale "is typing" if the end event drops.
          if (typingClearTimer.current) window.clearTimeout(typingClearTimer.current);
          typingClearTimer.current = window.setTimeout(() => setTypingFrom(null), 8000);
        },
        onTypingEnded: (typing: CometChat.TypingIndicator) => {
          if (typingMatches(typing)) setTypingFrom(null);
        },
        onMessagesDelivered: (receipt: CometChat.MessageReceipt) => {
          if (!receiptMatches(receipt)) return;
          const mid = Number(receipt.getMessageId());
          if (Number.isFinite(mid)) setDeliveredUpTo((prev) => Math.max(prev, mid));
        },
        onMessagesRead: (receipt: CometChat.MessageReceipt) => {
          if (selection.kind === "group") {
            if (receipt.getReceiverType() === "group" && receipt.getReceiver() === selection.id) {
              setGroupReadPulse((p) => p + 1);
            }
            return;
          }
          if (!receiptMatches(receipt)) return;
          const mid = Number(receipt.getMessageId());
          if (Number.isFinite(mid)) setReadUpTo((prev) => Math.max(prev, mid));
        },
      })
    );

    return () => {
      cancelled = true;
      CometChat.removeMessageListener(id);
      if (typingClearTimer.current) window.clearTimeout(typingClearTimer.current);
    };
  }, [selection]);

  const addLocal = useCallback((msg: CometChat.BaseMessage) => {
    setMessages((prev) => {
      if (prev.some((m) => m.getId() === msg.getId())) return prev;
      return [...prev, msg];
    });
  }, []);

  // For the sender's own edit/delete — CometChat.editMessage/deleteMessage
  // return the updated message, but don't fire the sender's own real-time
  // listener, so we apply it to local state ourselves.
  const updateLocal = useCallback((msg: CometChat.BaseMessage) => {
    setMessages((prev) => prev.map((m) => (m.getId() === msg.getId() ? msg : m)));
  }, []);

  return { messages, loading, addLocal, updateLocal, typingFrom, deliveredUpTo, readUpTo, groupReadPulse };
}
